import React, { Component } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap";

import { FetchData } from "./utils/DataFetch";
import { GetCookie } from "./utils/Common";
import { lineUnitCost } from "./translationLine";

import "./Translation.css";

const toNumberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));

/**
 * Adds or edits one quote line (a JobTransQuoteLine row) on a translation job. Unit cost
 * follows word count × cost per unit until it's typed over.
 */
export class TranslationBillingForm extends Component {
  static displayName = TranslationBillingForm.name;

  constructor(props) {
    super(props);
    this.user = JSON.parse(GetCookie("user"));
    this.state = {
      model: props.model,
      alert: { color: "warning", message: "", isOpen: false },
      saving: false,
    };
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => {
      const model = { ...prev.model, [name]: value };
      if (name === "WordCount" || name === "CostPerUnit") {
        const computed = lineUnitCost(model.WordCount, model.CostPerUnit);
        if (computed !== null) model.Amount = computed;
      }
      return { model };
    });
  };

  warn = (message) => this.setState({ alert: { color: "warning", message, isOpen: true } });

  onSave = () => {
    const { model } = this.state;
    const isInsert = model.EventType === "insert";
    const amount = model.Amount === "" || model.Amount === null ? NaN : Number(model.Amount);
    const wordCount = model.WordCount === "" || model.WordCount === null ? null : Number(model.WordCount);

    if (wordCount !== null && !Number.isInteger(wordCount)) {
      this.warn("Word count must be a whole number.");
      return;
    }
    if (!Number.isFinite(amount)) {
      this.warn("Enter a unit cost, or a word count and cost per unit.");
      return;
    }

    const payload = {
      EventType: model.EventType,
      JobTransQuoteLineId: model.JobTransQuoteLineId,
      JobId: model.JobId,
      Subject: model.Subject.trim(),
      Language: model.Language.trim(),
      WordCount: wordCount,
      CostPerUnit: toNumberOrNull(model.CostPerUnit),
      Amount: amount,
      IsDeletedFlg: model.IsDeletedFlg,
      CreateByUserId: isInsert ? this.user.userid : model.CreateByUserId,
      UpdateByUserId: isInsert ? null : this.user.userid,
    };

    this.setState({ saving: true });
    FetchData("/savejobtransquoteline", "POST", this.user.token, payload).then((data) => {
      this.setState({ saving: false });
      if (data === "Success") {
        this.props.onSaved(isInsert ? "Quote line added." : "Quote line updated.");
      } else {
        this.warn(data || "Could not save the quote line.");
      }
    });
  };

  render() {
    const { model, alert, saving } = this.state;
    const isInsert = model.EventType === "insert";

    return (
      <Modal isOpen toggle={this.props.onCancel} modalClassName="tr-modal" backdrop="static">
        <ModalHeader toggle={this.props.onCancel}>
          <span className="tr-modal-title">{isInsert ? "Add quote line" : "Edit quote line"}</span>
        </ModalHeader>
        <ModalBody>
          <Alert color={alert.color} isOpen={alert.isOpen} role="alert">
            {alert.message}
          </Alert>
          <section className="tr-section">
            <div className="row g-2 mb-3">
              <div className="col-md-8">
                <label className="form-label" htmlFor="tr-bill-subject">
                  Subject
                </label>
                <input
                  id="tr-bill-subject"
                  type="text"
                  name="Subject"
                  maxLength={200}
                  className="form-control"
                  value={model.Subject}
                  onChange={this.handleChange}
                  placeholder="e.g. Above Captioned"
                  autoFocus
                />
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="tr-bill-language">
                  Language
                </label>
                <input
                  id="tr-bill-language"
                  type="text"
                  name="Language"
                  maxLength={100}
                  className="form-control"
                  value={model.Language}
                  onChange={this.handleChange}
                />
              </div>
            </div>
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <label className="form-label" htmlFor="tr-bill-words">
                  Target word count
                </label>
                <input
                  id="tr-bill-words"
                  type="number"
                  step="1"
                  name="WordCount"
                  className="form-control"
                  value={model.WordCount ?? ""}
                  onChange={this.handleChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label" htmlFor="tr-bill-cost">
                  Cost per unit
                </label>
                <input
                  id="tr-bill-cost"
                  type="number"
                  step="0.001"
                  name="CostPerUnit"
                  className="form-control"
                  value={model.CostPerUnit ?? ""}
                  onChange={this.handleChange}
                  placeholder="e.g. 0.216"
                />
              </div>
            </div>
            <div>
              <label className="form-label" htmlFor="tr-bill-amount">
                Unit cost
              </label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  id="tr-bill-amount"
                  type="number"
                  step="0.01"
                  name="Amount"
                  className="form-control"
                  value={model.Amount ?? ""}
                  onChange={this.handleChange}
                  aria-describedby="tr-bill-amount-hint"
                />
              </div>
              <p id="tr-bill-amount-hint" className="tr-hint">
                Fills in as word count × cost per unit. Use a negative word count for a discount.
              </p>
            </div>
          </section>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={this.props.onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={this.onSave} disabled={saving}>
            {saving ? "Saving…" : isInsert ? "Add line" : "Save changes"}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}
